import { Fragment, useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import LoadingPage from "../common/LoadingPage";
import ErrorPage from "../common/ErrorPage";
import { useCutoffSearchContext } from "../../store/CutoffSearchContextProvider";
import { CutoffFilterMap } from "./FilterComponents/FilterConstants";
import { CutoffsVerticalPanel } from "./FilterComponents/CutoffsVerticalPanel";
import { checkApi } from "../../Authentication/AxiosInstance";
import { Accordion, AccordionHeader, AccordionBody, Button, Input } from "@material-tailwind/react";
import { ChevronDownIcon, FunnelIcon, ArrowUpIcon, ArrowDownIcon, BarsArrowUpIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

export const CutoffsMobileFirst = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [includePWD, setIncludePWD] = useState(false);
  const itemsPerPage = 10;
  
  const filterMap = CutoffFilterMap;
  const searchObject = useCutoffSearchContext().cutoffSearchArray;
  const getSearchKey = (searchObject) => JSON.stringify(searchObject);
  const searchArray = [searchObject];

  const fetchCutoffs = async () => {
    const searchParams = createSearchParams(searchArray);
    const params = Object.fromEntries(searchParams);
    const res = await checkApi.get('/capzone/cutoffs/',{params});
    return res.data;
  };

  const { data, error, status } = useQuery({
    queryKey: ["cutoffData", getSearchKey(searchObject)],
    queryFn: fetchCutoffs,
    throwOnError: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  // Reset pagination when data, search, or PWD filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [data, searchTerm, includePWD]);

  // Handle sort click
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(0);
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <BarsArrowUpIcon className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 text-primary-600" />
      : <ArrowDownIcon className="h-4 w-4 text-primary-600" />;
  };

  function createSearchParams(params) {
    const searchParams = new URLSearchParams();
    params.forEach((item) => {
      Object.keys(item).forEach((paramKey) => {
        if (item[paramKey]) {
          const findCutoffParent = filterMap.find((cutoffKey) => cutoffKey.parent === paramKey);
          const paramCutoffName = findCutoffParent ? findCutoffParent.cutoffs : null;
          if (paramCutoffName) {
            const suffix = paramCutoffName !== "max_round" ? "__icontains" : "";
            searchParams.append(paramCutoffName + suffix, item[paramKey]);
          }
        }
      });
    });
    return searchParams;
  }

  const ShowSearchCriteria = () => {
    return Object.entries(searchObject).map(([key, value]) => {
      if (value !== null && value !== undefined) {
        return (
          <div key={key} className="inline-flex items-center px-2 py-1 rounded-full bg-primary-100 text-primary-900 text-xs font-semibold border border-primary-300">
            <span>{value}</span>
            <span className="ml-1 text-primary-600">&#x2713;</span>
          </div>
        );
      }
    });
  };

  // Icon component for Accordion
  function Icon({ id, open }) {
    return (
      <ChevronDownIcon
        className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`}
      />
    );
  }

  // Card component for each cutoff entry
  const CutoffCard = ({ cutoff, index, allocationBoard }) => {
    const closingRank = cutoff.c001_clrank || 0;
    const openingRank = cutoff.c001_oprank || null;
    
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 mb-3 hover:shadow-lg transition-shadow">
        {/* Institute Name - Prominent */}
        <div className="mb-3">
          <h3 className="text-base font-bold text-primary-900 leading-tight">
            {cutoff.c001_instname}
          </h3>
          <p className="text-xs text-gray-500 mt-1">Code: {cutoff.c001_instcode}</p>
        </div>

        {/* Branch */}
        <div className="mb-3 pb-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-secondary-900">
            {cutoff.c001_branchcode ? `(${cutoff.c001_branchcode}) ` : ''}{cutoff.c001_branchname}
          </p>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {cutoff.c001_statequota && (
            <div>
              <p className="text-xs text-gray-500">State Quota</p>
              <p className="text-sm font-medium text-secondary-900">{cutoff.c001_statequota}</p>
            </div>
          )}
          {cutoff.c001_common && (
            <div>
              <p className="text-xs text-gray-500">Common</p>
              <p className="text-sm font-medium text-secondary-900">{cutoff.c001_common || 'N/A'}</p>
            </div>
          )}
          {allocationBoard === "MHTCET-MH" && cutoff.c001_stage && (
            <div>
              <p className="text-xs text-gray-500">Stage</p>
              <p className="text-sm font-medium text-secondary-900">{cutoff.c001_stage}</p>
            </div>
          )}
          {allocationBoard === "MHTCET-AI" && cutoff.c001_summary && (
            <div>
              <p className="text-xs text-gray-500">Summary</p>
              <p className="text-sm font-medium text-secondary-900">{cutoff.c001_summary || 'N/A'}</p>
            </div>
          )}
          {allocationBoard && (allocationBoard === 'JOSAA' || allocationBoard === 'CSAB') && openingRank !== null && (
            <div>
              <p className="text-xs text-gray-500">Opening Rank</p>
              <p className="text-sm font-medium text-secondary-900">{openingRank}</p>
            </div>
          )}
          {allocationBoard && allocationBoard.includes('MHTCET') && cutoff.c001_clptile && (
            <div>
              <p className="text-xs text-gray-500">Closing Percentile</p>
              <p className="text-sm font-medium text-secondary-900">{cutoff.c001_clptile}</p>
            </div>
          )}
        </div>

        {/* Closing Rank - Highlighted */}
        <div className="mt-3 pt-3 border-t-2 border-primary-200 bg-primary-50 rounded p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-600">Closing Rank</span>
            <span className="text-2xl font-bold text-primary-900">{closingRank}</span>
          </div>
        </div>
      </div>
    );
  };

  const formatReleaseDate = (dateString) => {
    if (!dateString) return "No Date";
    const dateObj = new Date(dateString);
    return dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  
  // Filter data based on PWD checkbox
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (includePWD) {
      return data.filter((item) => item.c001_common === "PWD");
    } else {
      return data.filter((item) => item.c001_common !== "PWD");
    }
  }, [data, includePWD]);

  // Sort and filter data
  const sortedData = useMemo(() => {
    if (!filteredData) return [];
    
    let filtered = [...filteredData];
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((cutoff) => {
        // Search in institute name
        const instName = (cutoff.c001_instname || '').toLowerCase();
        // Search in branch name
        const branchName = (cutoff.c001_branchname || '').toLowerCase();
        // Search in opening rank (if exists)
        const openingRank = cutoff.c001_oprank ? String(cutoff.c001_oprank) : '';
        // Search in closing rank
        const closingRank = cutoff.c001_clrank ? String(cutoff.c001_clrank) : '';
        
        return (
          instName.includes(searchLower) ||
          branchName.includes(searchLower) ||
          openingRank.includes(searchTerm.trim()) ||
          closingRank.includes(searchTerm.trim())
        );
      });
    }
    
    // Apply sorting
    if (sortBy) {
      filtered.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';
        
        // Handle numeric values (ranks, percentiles)
        if (sortBy === 'c001_clrank' || sortBy === 'c001_oprank' || sortBy === 'c001_clptile') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // Handle string values
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [filteredData, sortBy, sortOrder, searchTerm]);

  // Pagination logic
  const totalPages = sortedData ? Math.ceil(sortedData.length / itemsPerPage) : 0;
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData ? sortedData.slice(startIndex, endIndex) : [];

  return (
    <Fragment>
      <div className="flex flex-col w-full min-h-screen bg-gray-50">
        {status === "pending" ? (
          <div className="flex w-full justify-center items-center min-h-[60vh]">
            <LoadingPage invokedFrom="cutoffs" />
          </div>
        ) : status === "error" ? (
          <div className="w-full">
            <ErrorPage 
              emessage={
                error?.response?.data?.detail || 
                error?.response?.data?.message || 
                error?.message || 
                "An error occurred while fetching cutoffs data"
              } 
              invokedFrom="cutoffs" 
            />
          </div>
        ) : (
          filteredData && filteredData.length > 0 ? (
            <div className="w-full">
              {/* Sticky Header Section */}
              <div className="sticky top-0 z-10 bg-white shadow-md border-b border-gray-200">
                <div className="p-3 space-y-3">
                  {/* Collapsible Filter Section */}
                  <Accordion open={filterOpen} icon={<Icon id="filter" open={filterOpen} />}>
                    <AccordionHeader
                      onClick={() => setFilterOpen(!filterOpen)}
                      className="border-2 border-primary-500 rounded-lg shadow-sm py-3 px-4 bg-primary-50 hover:bg-primary-100 transition-colors"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <FunnelIcon className="h-5 w-5 text-primary-600" />
                          <span className="text-sm font-semibold text-primary-900">Filters</span>
                          {Object.keys(searchObject).some(key => searchObject[key] !== null && searchObject[key] !== undefined) && (
                            <span className="bg-primary-500 text-white text-xs rounded-full px-2 py-0.5 font-semibold">
                              {Object.values(searchObject).filter(v => v !== null && v !== undefined).length}
                            </span>
                          )}
                        </div>
                      </div>
                    </AccordionHeader>
                    <AccordionBody className="pt-4 pb-4 px-2">
                      <div className="bg-white rounded-lg border border-primary-200 p-3">
                        <CutoffsVerticalPanel />
                      </div>
                    </AccordionBody>
                  </Accordion>

                  {/* Search Criteria Chips */}
                  {Object.keys(searchObject).some(key => searchObject[key] !== null && searchObject[key] !== undefined) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm">📌</span>
                      <div className="flex flex-wrap gap-2">
                        <ShowSearchCriteria/>
                      </div>
                    </div>
                  )}

                  {/* Search Input */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Search by Institute, Branch, Opening Rank, or Closing Rank..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pr-10 text-sm"
                        containerProps={{
                          className: "min-w-0",
                        }}
                      />
                      <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    </div>
                    {searchTerm && (
                      <p className="text-xs text-gray-500 mt-2">
                        {sortedData.length} result{sortedData.length !== 1 ? 's' : ''} found
                      </p>
                    )}
                  </div>

                  {/* Release Date Display */}
                  <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-3 border-2 border-primary-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">Release Date:  &nbsp; 
                        <span className="text-sm text-gray-700 font-bold mt-1">{formatReleaseDate(filteredData[0]?.c001_releasedate)}</span></p>
                      </div>
                    </div>
                  </div>

                  {/* Display PWD Only checkbox */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <label className="flex items-center cursor-pointer">
                      <span className="text-sm font-semibold text-gray-700 mr-2">Display PWD Only?</span>
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 cursor-pointer"
                        style={{ accentColor: '#964734' }}
                        checked={includePWD}
                        onChange={() => setIncludePWD(!includePWD)}
                      />
                    </label>
                  </div>

                  {/* Sort Controls */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs font-semibold text-gray-700 mr-1">Sort by:</span>
                      <button
                        onClick={() => handleSort('c001_instname')}
                        className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                          sortBy === 'c001_instname'
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                        }`}
                      >
                        Institute
                        {getSortIcon('c001_instname')}
                      </button>
                      <button
                        onClick={() => handleSort('c001_branchname')}
                        className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                          sortBy === 'c001_branchname'
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                        }`}
                      >
                        Branch
                        {getSortIcon('c001_branchname')}
                      </button>
                      <button
                        onClick={() => handleSort('c001_clrank')}
                        className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                          sortBy === 'c001_clrank'
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                        }`}
                      >
                        Closing Rank
                        {getSortIcon('c001_clrank')}
                      </button>
                      {filteredData && filteredData[0]?.c001_allocationboard && (filteredData[0].c001_allocationboard === 'JOSAA' || filteredData[0].c001_allocationboard === 'CSAB') && (
                        <button
                          onClick={() => handleSort('c001_oprank')}
                          className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                            sortBy === 'c001_oprank'
                              ? 'bg-primary-500 text-white border-primary-500'
                              : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                          }`}
                        >
                          Opening Rank
                          {getSortIcon('c001_oprank')}
                        </button>
                      )}
                      {filteredData && filteredData[0]?.c001_allocationboard && filteredData[0].c001_allocationboard.includes('MHTCET') && (
                        <button
                          onClick={() => handleSort('c001_clptile')}
                          className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                            sortBy === 'c001_clptile'
                              ? 'bg-primary-500 text-white border-primary-500'
                              : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                          }`}
                        >
                          Percentile
                          {getSortIcon('c001_clptile')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards Section */}
              <div className="p-3 pb-20">
                {paginatedData.length > 0 ? (
                  <div className="space-y-3">
                    {paginatedData.map((cutoff, index) => (
                      <CutoffCard 
                        key={index} 
                        cutoff={cutoff} 
                        index={startIndex + index}
                        allocationBoard={filteredData[0]?.c001_allocationboard}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-base">No cutoffs found</p>
                    <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
                  </div>
                )}
              </div>

              {/* Pagination - Sticky Bottom */}
              {totalPages > 1 && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10">
                  <div className="flex items-center justify-between max-w-md mx-auto p-3">
                    <Button
                      size="sm"
                      variant="outlined"
                      className="px-4 py-2 text-xs"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </Button>
                    <span className="text-sm font-semibold text-gray-700">
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outlined"
                      className="px-4 py-2 text-xs"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                      disabled={currentPage >= totalPages - 1}
                    >
                      Next
                    </Button>
                  </div>
                  {/* Disclaimer - Below Pagination Footer */}
                  <div className="px-3 pb-3 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-600 italic text-center leading-relaxed">
                      <span className="font-semibold text-gray-700">🔔 Disclaimer:</span> The information presented on this site is intended solely for general reference. While we strive to aggregate important notices from various examination boards in one convenient location, students are advised to refer to the official board websites for the most accurate, detailed, and up-to-date information. 📚
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-center items-center min-h-[60vh]">
              <div className="text-center text-lg font-bold text-primary-500 m-8 p-2">
                No data found for the selected combination. Please narrow your parameters and try again.
              </div>
            </div>
          )
        )}
      </div>
    </Fragment>
  );
};

